import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout from 'layouts/SidebarLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';

import CreateUserDialogButton from './CreateUserDialogButton';
import UpsertUserDialogButton from './UpsertUserDialogButton';

type User = {
  id: string;
  fullName: string;
  email: string;
  status: 'INACTIVE' | 'ACTIVE';
  role: 'EMPLOYEE' | 'ADMIN' | 'SUPER_ADMIN';
  createdAt: string;
};

type DepartmentQueryResponse = {
  users: {
    nodes: User[];
    pageInfo: {
      cursor: string;
      totalCount: number;
      hasNextPage: boolean;
    };
  };
};

type DepartmentQueryVariables = {
  limit?: number;
  cursor?: string;
  filters?: {
    roles: ('EMPLOYEE' | 'ADMIN' | 'SUPER_ADMIN')[];
  };
};

const USERS_QUERY = gql`
  query AdministrationUsers($limit: Int, $cursor: ID, $filters: UserFilterInputType) {
    users(limit: $limit, cursor: $cursor, filters: $filters) {
      nodes {
        id
        fullName
        email
        status
        role
        createdAt
      }
      pageInfo {
        cursor
        totalCount
        hasNextPage
      }
    }
  }
`;

const AdministrationUsers: FC = () => {
  const navigate = useNavigate();
  const [userOnDisplay, setUserOnDisplay] = useState('');

  const { loading, error, data, refetch, fetchMore, networkStatus } = useQuery<
    DepartmentQueryResponse,
    DepartmentQueryVariables
  >(USERS_QUERY, {
    variables: {
      limit: 30,
      filters: {
        roles: ['ADMIN', 'SUPER_ADMIN'],
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  function renderContent() {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const users = data.users.nodes;
    const pageInfo = data.users.pageInfo;

    return (
      <>
        <h2>Super Admins and Admins (for the platform)</h2>
        <CreateUserDialogButton onClose={() => {}} />
        {userOnDisplay && (
          <UpsertUserDialogButton userId={userOnDisplay} onClose={() => setUserOnDisplay('')} />
        )}
        <DataTable
          data={users}
          onClick={user => setUserOnDisplay(user.id)}
          columns={[
            {
              label: 'Full Name',
              fieldName: 'fullName',
            },
            {
              label: 'Email',
              fieldName: 'email',
            },
            {
              label: 'Role',
              fieldName: 'role',
            },
            {
              label: 'Created At',
              fieldName: 'createdAt',
              type: 'DATETIME',
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'STATUS',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          paginationLoading={loading}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
          totalCount={pageInfo.totalCount}
        />
      </>
    );
  }

  return (
    <SidebarLayout>
      <Navbar
        title="Users (Admin Only)"
        subTitle="Manage the administration users"
        onBackButtonClick={() => navigate(-1)}
      />
      {renderContent()}
    </SidebarLayout>
  );
};

export default AdministrationUsers;
